import { useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import AuthCard from "@/components/auth/AuthCard";
import AuthInput from "@/components/auth/AuthInput";
import AuthButton from "@/components/auth/AuthButton";
import AuthLink from "@/components/auth/AuthLink";
import { useAuth, formatAuthError } from "@/context/AuthContext";

const EMAIL_RULE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const REQ_TEXT = "Password must be at least 8 characters and include a mix of letters, numbers, and symbols.";

// Password requirement checks. Returned as a stable-order array so the
// checklist renders deterministically.
function checkPassword(pw) {
  return [
    { key: "len",     label: "At least 8 characters",    ok: pw.length >= 8 },
    { key: "letter",  label: "Contains a letter",        ok: /[A-Za-z]/.test(pw) },
    { key: "number",  label: "Contains a number",        ok: /\d/.test(pw) },
    { key: "symbol",  label: "Contains a symbol",        ok: /[^A-Za-z0-9]/.test(pw) },
  ];
}

function strengthOf(pw) {
  if (!pw) return { level: 0, label: "" };
  const passed = checkPassword(pw).filter((c) => c.ok).length;
  // 0 empty → 1-2 Weak → 3 Moderate → 4 Strong
  if (passed <= 2) return { level: 1, label: "Weak"     };
  if (passed === 3) return { level: 2, label: "Moderate" };
  return { level: 3, label: "Strong" };
}

export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [confirmError, setConfirmError] = useState("");
  const [busy, setBusy] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const checks = useMemo(() => checkPassword(password), [password]);
  const strength = useMemo(() => strengthOf(password), [password]);
  const allChecksPass = checks.every((c) => c.ok);
  const showMismatch = confirm.length > 0 && confirm !== password;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setConfirmError("");
    const trimmedEmail = email.trim().toLowerCase();
    if (!EMAIL_RULE.test(trimmedEmail)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!allChecksPass) {
      setError("Your password is too weak.");
      return;
    }
    if (password !== confirm) {
      setConfirmError("Passwords do not match.");
      return;
    }
    if (!agreed) {
      setError("Please agree to the Terms of Use and Privacy Policy to continue.");
      return;
    }
    setBusy(true);
    try {
      await signup(trimmedEmail, password);
      toast.success("Account created — you can sign in now.");
      navigate("/auth/login", { replace: true });
    } catch (err) {
      setError(formatAuthError(err));
    } finally { setBusy(false); }
  };

  return (
    <AuthCard
      title="Welcome to CDI Collectables"
      subtitle="Your 3-day free trial starts immediately."
      footer={<>Already have an account? <AuthLink to="/auth/login" testid="signup-link-login">Sign in</AuthLink></>}
    >
      <form onSubmit={onSubmit} noValidate>
        <AuthInput label="Email" type="email" required
          testid="signup-input-email" value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com" autoComplete="email"
          error={error && error.startsWith("Please enter a valid email") ? error : ""} />

        {/* Password with Show toggle + strength + checklist */}
        <div className="mb-4">
          <div className="relative">
            <AuthInput
              label="Password"
              type={showPw ? "text" : "password"}
              required
              testid="signup-input-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Create a password"
              autoComplete="new-password"
              error={error === "Your password is too weak." ? error : ""}
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              aria-label={showPw ? "Hide password" : "Show password"}
              data-testid="signup-toggle-password"
              className="absolute flex items-center justify-center"
              style={{
                right: 4, top: 24, width: 40, height: 40,
                background: "transparent", border: "none",
                color: "#c9c9c9", cursor: "pointer", padding: 0,
              }}
            >
              {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          {/* Requirements text */}
          <p className="text-xs mt-2" style={{ color: "#9a9a9a", lineHeight: 1.5 }}
             data-testid="signup-password-requirements-text">
            {REQ_TEXT}
          </p>

          {/* Strength meter + label */}
          {password && (
            <div className="mt-2" data-testid="signup-password-strength">
              <div className="flex gap-1" aria-hidden>
                {[1, 2, 3].map((seg) => (
                  <div
                    key={seg}
                    style={{
                      height: 4, flex: 1, borderRadius: 2,
                      background:
                        strength.level >= seg
                          ? (strength.level === 1 ? "#c86a6a"
                             : strength.level === 2 ? "#e0b34a"
                             : "#7ab97a")
                          : "#2a2a2a",
                      transition: "background 120ms ease",
                    }}
                  />
                ))}
              </div>
              <div
                className="text-xs mt-1 font-medium"
                data-testid="signup-password-strength-label"
                style={{
                  color:
                    strength.level === 1 ? "#c86a6a"
                    : strength.level === 2 ? "#e0b34a"
                    : "#7ab97a",
                }}
              >
                {strength.label}
              </div>
            </div>
          )}

          {/* Live checklist */}
          {password && (
            <ul className="mt-2 space-y-1" data-testid="signup-password-checks">
              {checks.map((c) => (
                <li
                  key={c.key}
                  data-testid={`signup-password-check-${c.key}`}
                  className="text-xs flex items-center gap-2"
                  style={{ color: c.ok ? "#7ab97a" : "#8a8a8a" }}
                >
                  <span aria-hidden style={{
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    width: 14, height: 14, borderRadius: 999, flexShrink: 0,
                    background: c.ok ? "#7ab97a" : "transparent",
                    border: c.ok ? "none" : "1px solid #555",
                    color: "#000", fontSize: 10, fontWeight: 900,
                  }}>{c.ok ? "✓" : ""}</span>
                  {c.label}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Confirm password with Show toggle + mismatch validation */}
        <div className="mb-2">
          <div className="relative">
            <AuthInput
              label="Confirm password"
              type={showConfirm ? "text" : "password"}
              required
              testid="signup-input-confirm"
              value={confirm}
              onChange={(e) => { setConfirm(e.target.value); setConfirmError(""); }}
              placeholder="Repeat password"
              autoComplete="new-password"
              error={confirmError || (showMismatch ? "Passwords do not match." : "")}
            />
            <button
              type="button"
              onClick={() => setShowConfirm((v) => !v)}
              aria-label={showConfirm ? "Hide password" : "Show password"}
              data-testid="signup-toggle-confirm"
              className="absolute flex items-center justify-center"
              style={{
                right: 4, top: 24, width: 40, height: 40,
                background: "transparent", border: "none",
                color: "#c9c9c9", cursor: "pointer", padding: 0,
              }}
            >
              {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        {/* Terms & Privacy acceptance — required for account creation. */}
        <label
          className="flex items-start gap-2 text-xs mb-3"
          style={{ color: "#c9c9c9", lineHeight: 1.5 }}
          data-testid="signup-legal-acceptance"
        >
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            data-testid="signup-legal-checkbox"
            required
            style={{ marginTop: 2, accentColor: "#C9A86A", flexShrink: 0 }}
          />
          <span>
            I confirm that I am 18 years or older, I am not located in and not ordinarily resident in the United Kingdom or the European Union, and I have read, understand and agree to the{" "}
            <Link to="/legal/terms" data-testid="signup-legal-terms-link" style={{ color: "#C9A86A", textDecoration: "underline" }}>
              Terms of Use
            </Link>{" "}
            and{" "}
            <Link to="/legal/privacy" data-testid="signup-legal-privacy-link" style={{ color: "#C9A86A", textDecoration: "underline" }}>
              Privacy Policy
            </Link>
            .
          </span>
        </label>

        <AuthButton type="submit" testid="signup-submit" loading={busy} disabled={!agreed}>Create account</AuthButton>
      </form>
    </AuthCard>
  );
}
